import { cx, api, rx } from "../../../../api";
import { ofType, combineEpics } from 'redux-observable';
import { switchMap, takeUntil, map as rxmap, filter, tap, ignoreElements } from 'rxjs/operators';
import { formatDateTime, getTimezoneString } from '../../../../misc/misc';
import { resolver } from './resolver';
import { actions } from '../actions';
import { fc, f } from "../../../../../i18n";
import { errorMap } from "../../../actions";
import { getEventDetails } from '../../../../../helpers/getEventDetails';
import { getAssetType } from "../../../../misc/assetTypes";

const requestEpic = (action$) => {
	return action$.pipe(
		ofType(actions.deviceSituations.request.type),
		switchMap(action =>
			rx(api.reports.deviceSituations, action.parameters).pipe(
				rxmap(operation => actions.deviceSituations.success({ situations: operation.response() })),
				errorMap(actions.deviceSituations.fail),
				takeUntil(action$.pipe(ofType(actions.deviceSituations.cancel.type)))
			)
		)
	)
}

const startPrepareEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.deviceSituations.export.type),
		rxmap(action => {
			const state = state$.value.reports.deviceSituations;
			resolver.resolve(state.list, !state.hasMore);
			if (state.hasMore) {
				return actions.deviceSituations.request({ parameters: state.parameters });
			} else {
				return actions.deviceSituations.success({});
			}
		})
	)
}

const processPrepareEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.deviceSituations.success.type),
		filter(action => {
			const state = state$.value.reports.deviceSituations;
			if (state.exporting) resolver.resolve(action.situations, !state.hasMore);
			return state.exporting && state.hasMore;
		}),
		rxmap(action => {
			const state = state$.value.reports.deviceSituations;
			return actions.deviceSituations.request({ parameters: state.parameters });
		})
	)
}

const exportCsvEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.deviceSituations.exportProgress.type),
		filter(action => action.progress == 100),
		rxmap(action => {
			const state = state$.value.reports.deviceSituations;
			const eventTypes = state$.value.registry.eventTypes.typeMap;
			const isGroup = !!state.parameters.groupingId;
			const isAssetType = !!state.parameters.isAssetTypes;

			// First row: Report header with 5 columns and 5 empty columns (total 10)
			let csv = '"'
				+ fc('report type')
				+ '","'
				+ fc('generated')
				+ '","'
				+ fc('timezone')
				+ '","'
				+ fc('since')
				+ '","'
				+ fc('until')
				+ '","","","","",""\n';

			// Second row: Report data with 5 columns and 5 empty columns (total 10)
			csv += '"' + fc('device situations') + '"';  // report type
			csv += ',"' + formatDateTime(cx.now()) + '"'; // generated at
			csv += ',"' + getTimezoneString() + '"'; // timezone at
			csv += ',"' + formatDateTime(state.parameters.timeRange.since) + '"';
			csv += ',"' + formatDateTime(state.parameters.timeRange.until) + '"';
			csv += ',"","","","",""\n\n';

			// Third row: Content header with 10 columns (Group, Asset type, Time, URI, Device name, Event, Details, Location, Address, Marker)
			// Group, Asset type, and Marker are optional
			let contentHeader = '';

			// Add Group and Asset Type columns if needed
			if (isGroup) {
				contentHeader += '"' + fc('group') + '",';
			}

			if (isAssetType) {
				contentHeader += '"' + fc('asset type') + '",';
			}

			// Add base columns
			contentHeader += '"' + fc('time') + '","'
				+ f('URI') + '","'
				+ fc('device name')
				+ '","'
				+ fc('event')
				+ '","'
				+ fc('details')
				+ '","'
				+ fc('location')
				+ '","'
				+ fc('address')
				+ '"';

			// Add marker column if needed
			if (state.parameters.markers) {
				contentHeader += ',"' + fc('marker') + '"';
			}

			// We need to ensure that the content header has exactly 10 columns
			const columnsAdded = (isGroup ? 1 : 0) + (isAssetType ? 1 : 0) + 7 + (state.parameters.markers ? 1 : 0);
			const emptyColumnsNeeded = 10 - columnsAdded;

			// Add empty columns to ensure we have 10 columns total
			for (let i = 0; i < emptyColumnsNeeded; i++) {
				contentHeader += ',""';
			}

			csv += contentHeader + "\n";
			// content
			state.list.forEach(situation => {
				const device = state$.value.devices.map[situation.uri];

				// Calculate group value if needed
				let group = '-';
				if (isGroup && device.categoryIds) {
					const categoryMap = state$.value.categories.general.map;
					device.categoryIds.forEach(categoryId => {
						const category = categoryMap[categoryId];
						const comprisingIds = category?.comprisingIds;
						if (comprisingIds && state.parameters.groupingId && comprisingIds.includes(state.parameters.groupingId)) {
							group = category.name;
						}
					});
				}

				// Calculate asset type if needed
				const assetType = isAssetType ? getAssetType(device.categoryIds)?.getName() || '-' : null;

				situation.events && situation.events.forEach(event => {
					const eventType = eventTypes[event.eventType];
					const eventDetails = getEventDetails(event, eventType, state$.value.zones) || '-';

					let row = '';

					// Add Group and Asset Type values if needed
					if (isGroup) {
						row += '"' + group + '",';
					}

					if (isAssetType) {
						row += '"' + (assetType || '-') + '",';
					}

					// Add base columns
					row += '"' + formatDateTime(event.generatedAt) + '",'; // time
					row += '"' + device.uri + '",';
					row += '"' + (device.denomination() || '-') + '",';
					row += '"' + eventType.name + '",'; // event
					row += '"' + eventDetails + '",';

					if (event.message && event.message.latitude && event.message.longitude) { // location, address
						row += '"(' + event.message.latitude + ";" + event.message.longitude + ')",';
						const entryKey = resolver.key(event.message.latitude, event.message.longitude);
						const address = resolver.resolved[entryKey]?.getAddress();
						if (address) row += '"' + address.format() + '"';
						else row += '"-"';
					} else {
						row += '"-","-"';
					}

					// Add marker if needed
					if (state.parameters.markers) {
						row += ',"' + (situation.marker ? situation.marker.message.fields.mapped[19] : "-") + '"';
					}

					// Add empty columns to ensure we have 10 columns total
					const rowColumnsAdded = (isGroup ? 1 : 0) + (isAssetType ? 1 : 0) + 7 + (state.parameters.markers ? 1 : 0);
					const rowEmptyColumnsNeeded = 10 - rowColumnsAdded;

					for (let i = 0; i < rowEmptyColumnsNeeded; i++) {
						row += ',""';
					}

					csv += row + "\n";
				});
			});
			return actions.deviceSituations.exportDone({ csv });
		}),
	)
}

const exportClearEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.deviceSituations.exportClear.type),
		tap(() => resolver.clear()),
		ignoreElements()
	);
}

const epic = combineEpics(requestEpic, startPrepareEpic, processPrepareEpic, exportCsvEpic, exportClearEpic);

export { epic };
